import { Injectable, inject } from '@angular/core';
import { injectQueryClient, mutationOptions, queryOptions } from '@tanstack/angular-query-experimental';
import type { QueryCacheNotifyEvent } from '@tanstack/query-core';
import { BackendService } from './backend.service';
import { Observable, firstValueFrom, from } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { GroupService } from './group.service';
import { Bucket, GroupPage, Fund, Page } from '../types';
import { MonthService } from './month.service';
import {
  BucketBalance,
  BucketMonth,
  BucketMonthPage,
  CreateFundTransfersRequest,
  CreateBucketRequest as ApiCreateBucketRequest,
  SuccessResponse,
  UpdateBucketRequest as ApiUpdateBucketRequest,
} from '../generated-api/models';
import { bucketQueryKeys, groupQueryKeys, transactionQueryKeys } from './query-keys';

const UNBUDGETED_BUCKET_ID = '000000000000000000000000';

type ApiUpdateBucketPayload = Omit<Partial<ApiUpdateBucketRequest>, 'fund'> & {
  fund?: ApiCreateBucketRequest['fund'];
};
export type BucketUpdateDraft = Pick<Bucket, 'id' | 'monthYear'> & {
  name?: string;
  type?: Bucket['type'];
  excludedFromSpendingReports?: boolean;
  amount?: number;
  fund?: Partial<Pick<Fund, 'start' | 'initialAmount' | 'goal'>>;
  groupId?: string | null;
  updateAmountForThisMonthOnly?: boolean;
  isSetup?: boolean;
};

export function toBucketUpdateDraft(
  bucket: Pick<Bucket, 'id' | 'monthYear'>,
  changes: Omit<BucketUpdateDraft, 'id' | 'monthYear'> = {},
): BucketUpdateDraft {
  return {
    id: bucket.id,
    monthYear: bucket.monthYear,
    ...changes,
  };
}

export type ArchiveBucketMutationVariables = {
  bucketId: string;
  transferBalanceBucketId?: string;
};
export type TransferBudgetMutationVariables = {
  activeBucketId?: string;
  amount: number;
  direction: 'in' | 'out';
  monthYear?: string;
  /** Record the transfer in this past `YYYY-MM` month; omit for the current month. */
  budgetMonth?: string;
  transferBucketId: string;
};

@Injectable({ providedIn: 'root' })
export class BucketService {
  private backend = inject(BackendService);
  private groupService = inject(GroupService);
  private month = inject(MonthService);

  private readonly queryClient = injectQueryClient();

  allCurrentMonthBuckets$: Observable<Bucket[]> = this.month.currentMonthYear$.pipe(
    switchMap(monthYear => this.observeGroupPageBuckets(monthYear)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  nonArchivedBuckets$ = this.allCurrentMonthBuckets$.pipe(
    map(buckets =>
      buckets
        .filter(bucket => !bucket.archivedMonth)
        .sort((a, b) => (a.name === b.name ? 0 : a.name < b.name ? -1 : 1)),
    ),
  );

  bucketsWithFunds$ = this.allCurrentMonthBuckets$.pipe(
    map(buckets => buckets.filter(bucket => (bucket.fund?.balance ?? 0) > 0)),
  );

  invalidateAllBuckets(refreshIfLoaded: boolean = true): void {
    const cachedPage = this.queryClient.getQueryData<Page<Bucket>>(bucketQueryKeys.list());
    void this.queryClient.invalidateQueries({ queryKey: bucketQueryKeys.list(), refetchType: 'none' });

    if (refreshIfLoaded && cachedPage) this.refreshAllBucketsInBackground();
  }

  private refreshAllBucketsInBackground(): void {
    void this.queryClient.prefetchQuery(this.allBucketsQueryOptions());
  }

  fetchBucket$(bucketId: string, monthYear = this.month.monthYearFromDate()): Observable<Bucket> {
    return from(this.queryClient.fetchQuery(this.bucketDetailQueryOptions(bucketId, monthYear)));
  }

  invalidateBucketDetail(bucketId?: string | null, monthYear?: string | null): void {
    if (!bucketId || !monthYear) return;

    void this.queryClient.invalidateQueries({
      queryKey: bucketQueryKeys.detail(bucketId, monthYear),
      refetchType: 'none',
    });
  }

  invalidateCurrentMonthBuckets(monthYear = this.month.currentMonthYear()): void {
    this.groupService.invalidateGroupPage(monthYear);
  }

  createBucketMutationOptions() {
    return mutationOptions({
      mutationKey: ['bucket', 'create'],
      mutationFn: (data: ApiCreateBucketRequest) => this.createBucket(data),
      onSuccess: bucket => this.setMutatedBucketData(bucket),
    });
  }

  updateBucketMutationOptions() {
    return mutationOptions({
      mutationKey: ['bucket', 'update'],
      mutationFn: (data: BucketUpdateDraft) => this.updateBucket(data),
      onSuccess: bucket => this.setMutatedBucketData(bucket),
    });
  }

  deleteBucketMutationOptions() {
    return mutationOptions({
      mutationKey: ['bucket', 'delete'],
      mutationFn: (bucketId: string) => this.deleteBucket(bucketId),
      onSuccess: () => this.invalidateCurrentBucketMutationCaches(),
    });
  }

  archiveBucketMutationOptions() {
    return mutationOptions({
      mutationKey: ['bucket', 'archive'],
      mutationFn: ({ bucketId, transferBalanceBucketId }: ArchiveBucketMutationVariables) =>
        this.archiveBucketRequest(bucketId, transferBalanceBucketId),
      onSuccess: response => this.setArchiveMutationData(response),
    });
  }

  unarchiveBucketMutationOptions() {
    return mutationOptions({
      mutationKey: ['bucket', 'unarchive'],
      mutationFn: (bucketId: string) => this.unarchiveBucketRequest(bucketId),
      onSuccess: bucket => this.setMutatedBucketData(bucket),
    });
  }

  transferBudgetMutationOptions() {
    return mutationOptions({
      mutationKey: ['fund-transfer', 'create'],
      mutationFn: (variables: TransferBudgetMutationVariables) => this.transferBudget(variables),
      onSuccess: (_response, variables) => {
        void this.queryClient.invalidateQueries({ queryKey: transactionQueryKeys.all });

        if (variables.monthYear) {
          this.invalidateBucketMutationCaches(variables.monthYear);
          return;
        }

        this.invalidateCurrentBucketMutationCaches();
      },
    });
  }

  createBucket(data: ApiCreateBucketRequest): Promise<Bucket> {
    return firstValueFrom(
      this.backend.request<Bucket & BucketMonth, ApiCreateBucketRequest>({
        type: 'post',
        apiRoute: 'bucket',
        data,
      }),
    );
  }

  updateBucket(data: BucketUpdateDraft): Promise<Bucket> {
    if (!data.monthYear) return Promise.reject(new Error('A month is required to update a budget.'));
    const payload = this.toUpdateBucketRequest(data);
    return firstValueFrom(
      this.backend.request<Bucket & BucketMonth, ApiUpdateBucketPayload>({
        type: 'update',
        apiRoute: `bucket/${data.id}`,
        data: payload,
      }),
    );
  }

  private toUpdateBucketRequest(
    data: BucketUpdateDraft,
  ): ApiUpdateBucketPayload & Pick<ApiUpdateBucketRequest, 'monthYear'> {
    return {
      monthYear: data.monthYear,
      ...(data.name !== undefined ? { name: data.name } : {}),
      ...(data.type !== undefined ? { type: data.type as ApiUpdateBucketRequest['type'] } : {}),
      ...(data.excludedFromSpendingReports !== undefined
        ? { excludedFromSpendingReports: data.excludedFromSpendingReports }
        : {}),
      ...(data.amount !== undefined ? { amount: data.amount } : {}),
      ...(data.fund?.start !== undefined && data.fund.initialAmount !== undefined
        ? {
            fund: {
              start: data.fund.start,
              initialAmount: data.fund.initialAmount,
              ...(data.fund.goal !== undefined ? { goal: data.fund.goal } : {}),
            },
          }
        : {}),
      ...(data.groupId !== undefined ? { groupId: data.groupId } : {}),
      ...(data.updateAmountForThisMonthOnly !== undefined
        ? { updateAmountForThisMonthOnly: data.updateAmountForThisMonthOnly }
        : {}),
      ...(data.isSetup !== undefined ? { isSetup: data.isSetup } : {}),
    };
  }

  deleteBucket$(bucketId: string): Observable<void> {
    return this.backend
      .request<void>({
        type: 'update',
        apiRoute: `bucket/${bucketId}/delete`,
      })
      .pipe(tap(() => this.invalidateCurrentBucketMutationCaches()));
  }

  deleteBucket(bucketId: string): Promise<void> {
    return firstValueFrom(
      this.backend.request<void>({
        type: 'update',
        apiRoute: `bucket/${bucketId}/delete`,
      }),
    );
  }

  archiveBucketTransferredBalanceQueryOptions(bucketId: string) {
    return queryOptions({
      queryKey: bucketQueryKeys.archiveBalance(bucketId),
      queryFn: () => this.archiveBucketTransferredBalanceRequest(bucketId),
      staleTime: 0,
    });
  }

  archiveBucketTransferredBalanceRequest(bucketId: string): Promise<BucketBalance> {
    return firstValueFrom(
      this.backend.request<BucketBalance>({
        type: 'get',
        apiRoute: `bucket/${bucketId}/archive`,
      }),
    );
  }

  archiveBucketRequest(
    bucketId: string,
    transferBalanceBucketId?: string,
  ): Promise<(Bucket & BucketMonth) | { success: boolean }> {
    return firstValueFrom(
      this.backend.request<(Bucket & BucketMonth) | { success: boolean }>({
        type: 'delete',
        apiRoute: `bucket/${bucketId}`,
        data: {
          unarchive: false,
          ...(transferBalanceBucketId === undefined ? {} : { transferBalanceBucketId }),
        },
      }),
    );
  }

  unarchiveBucketRequest(bucketId: string): Promise<Bucket> {
    return firstValueFrom(
      this.backend.request<Bucket & BucketMonth>({
        type: 'delete',
        apiRoute: `bucket/${bucketId}`,
        data: { unarchive: true },
      }),
    );
  }

  transferBudget(variables: TransferBudgetMutationVariables): Promise<SuccessResponse> {
    const from =
      variables.direction === 'in' ? variables.transferBucketId : (variables.activeBucketId ?? UNBUDGETED_BUCKET_ID);
    const to =
      variables.direction === 'in' ? (variables.activeBucketId ?? UNBUDGETED_BUCKET_ID) : variables.transferBucketId;
    // The API only accepts a positive amount with explicit from/to. A negative
    // amount means "move it the other way" — e.g. pull budgeted dollars back to
    // Available Balance — so swap the ends and send the magnitude.
    const rawAmount = variables.amount ?? 0;
    const reversed = rawAmount < 0;
    const transfer = {
      fromBucketId: reversed ? to : from,
      toBucketId: reversed ? from : to,
      amount: Math.abs(rawAmount),
    };

    return firstValueFrom(
      this.backend.request<SuccessResponse, CreateFundTransfersRequest>({
        type: 'post',
        apiRoute: 'fundTransfers',
        data: variables.budgetMonth
          ? { transfers: [transfer], budgetMonth: variables.budgetMonth }
          : { transfers: [transfer] },
      }),
    );
  }

  private setArchiveMutationData(response: (Bucket & BucketMonth) | { success: boolean }): void {
    if ('id' in response) {
      this.setMutatedBucketData(response);
      return;
    }

    this.invalidateCurrentBucketMutationCaches();
  }

  private setMutatedBucketData(bucket: Bucket): void {
    this.setBucketQueryData(bucket);
    this.invalidateBucketMutationCaches(bucket.monthYear);
  }

  private invalidateCurrentBucketMutationCaches(): void {
    this.groupService.invalidateCurrentGroupPage();
    this.invalidateSpendingTrend(this.month.currentMonthYear());
    this.invalidateBucketCollectionCaches();
  }

  private invalidateBucketMutationCaches(monthYear: string): void {
    this.groupService.invalidateGroupPage(monthYear);
    this.invalidateSpendingTrend(monthYear);
    this.invalidateBucketCollectionCaches();
  }

  private invalidateSpendingTrend(monthYear: string): void {
    void this.queryClient.invalidateQueries({ queryKey: groupQueryKeys.spendingTrend(monthYear) });
  }

  private invalidateBucketCollectionCaches(): void {
    this.invalidateAllBuckets();
  }

  allBucketsQueryOptions() {
    return queryOptions({
      queryKey: bucketQueryKeys.list(),
      queryFn: () => this.fetchAllBucketsPageFromBackend(),
      staleTime: 0,
    });
  }

  currentMonthBucketsQueryOptions(monthYear = this.month.monthYearFromDate()) {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    const groupPageQueryOptions = this.groupService.groupPageQueryOptions(normalizedMonthYear);
    return queryOptions({
      ...groupPageQueryOptions,
      select: page => this.bucketsFromGroupPage(page),
    });
  }

  bucketDetailQueryOptions(bucketId: string, monthYear = this.month.monthYearFromDate()) {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    return queryOptions({
      queryKey: bucketQueryKeys.detail(bucketId, normalizedMonthYear),
      queryFn: () => this.fetchBucketFromBackend(bucketId, normalizedMonthYear),
      staleTime: 0,
    });
  }

  private fetchAllBucketsPageFromBackend(): Promise<Page<Bucket>> {
    return firstValueFrom(
      this.backend.request<Page<Bucket> & BucketMonthPage>({
        type: 'get',
        apiRoute: `bucket`,
        params: { pageSize: 1000, includeArchived: true },
      }),
    );
  }

  private fetchBucketFromBackend(bucketId: string, monthYear: string): Promise<Bucket> {
    return firstValueFrom(
      this.backend.request<Bucket & BucketMonth>({
        type: 'get',
        apiRoute: `bucket/${bucketId}/${monthYear}`,
        params: { pageSize: 1000 },
      }),
    );
  }

  private setBucketQueryData(bucket: Bucket): Bucket {
    let syncedBucket = this.mergeBucketDetailQueryData(undefined, bucket);
    this.queryClient.setQueryData<Bucket>(bucketQueryKeys.detail(bucket.id, bucket.monthYear), currentBucket => {
      syncedBucket = this.mergeBucketDetailQueryData(currentBucket, bucket);
      return syncedBucket;
    });
    this.queryClient.setQueryData<Page<Bucket>>(bucketQueryKeys.list(), page => {
      if (!page) return page;
      const exists = page.data.some(existingBucket => existingBucket.id === bucket.id);
      const data = exists
        ? page.data.map(existingBucket =>
            existingBucket.id === bucket.id ? { ...existingBucket, ...bucket } : existingBucket,
          )
        : [...page.data, bucket];
      return { ...page, data };
    });
    return syncedBucket;
  }

  private mergeBucketDetailQueryData(currentBucket: Bucket | undefined, bucket: Bucket): Bucket {
    return {
      ...currentBucket,
      ...bucket,
      transactions: bucket.transactions ?? currentBucket?.transactions ?? this.emptyBucketTransactionPage(bucket),
    };
  }

  private emptyBucketTransactionPage(bucket: Bucket): Bucket['transactions'] {
    return {
      data: [],
      pageNumber: 1,
      pageSize: 25,
      totalRecords: 0,
      params: {
        bucketId: bucket.id,
        monthYear: bucket.monthYear,
        pageNumber: 1,
        pageSize: 25,
      },
      _objectType: 'transactionPage',
    };
  }

  private bucketsFromGroupPage(page: { data: Array<{ buckets: Bucket[] }> }): Bucket[] {
    return page.data.reduce<Bucket[]>((buckets, group) => [...buckets, ...group.buckets], []);
  }

  private observeGroupPageBuckets(monthYear: string): Observable<Bucket[]> {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    const queryKey = groupQueryKeys.page(normalizedMonthYear);

    return new Observable<Bucket[]>(subscriber => {
      let lastPage: GroupPage | undefined;
      const emitPage = (page: GroupPage | undefined) => {
        if (!page || page === lastPage) return;
        lastPage = page;
        subscriber.next(this.bucketsFromGroupPage(page));
      };

      emitPage(this.queryClient.getQueryData<GroupPage>(queryKey));

      const unsubscribe = this.queryClient.getQueryCache().subscribe(event => {
        if (!this.isQueryDataWriteEvent(event)) return;
        if (!this.matchesGroupPageQuery(event.query.queryKey, normalizedMonthYear)) return;
        emitPage(event.query.state.data as GroupPage | undefined);
      });

      void this.queryClient
        .fetchQuery(this.groupService.groupPageQueryOptions(normalizedMonthYear))
        .catch(error => {
          if (!subscriber.closed) subscriber.error(error);
        });

      return unsubscribe;
    });
  }

  private matchesGroupPageQuery(queryKey: readonly unknown[], monthYear: string): boolean {
    const expectedQueryKey = groupQueryKeys.page(monthYear);
    return (
      queryKey.length === expectedQueryKey.length && queryKey.every((value, index) => value === expectedQueryKey[index])
    );
  }

  private isQueryDataWriteEvent(event: QueryCacheNotifyEvent): boolean {
    return event.type === 'updated' && event.action.type === 'success';
  }

  private normalizeMonthYear(monthYear: string): string {
    return monthYear || this.month.monthYearFromDate();
  }

  // Bucket Helper Functions
  isIncomeAndBehind(bucket: Bucket) {
    return bucket.type === 'INCOME' && (bucket.fund?.balance ?? 0) < 0;
  }

  isIncomeAndWithinPaycheck(bucket: Bucket) {
    const balance = bucket.fund?.balance ?? 0;
    return bucket.type === 'INCOME' && balance < 0 && balance >= bucket.amount * -1;
  }

  getBalance(bucket: Bucket) {
    const balance = bucket.fund?.balance ?? 0;
    if (bucket.type === 'INCOME') return Math.abs(balance);
    return balance;
  }
}
